import { type DraftData, type FulfillmentFormValues } from '../model/types'

const draftKey = 'fulfillment-draft-v4'
/** Черновик до переезда прайса на новый договор сервера: читается один раз, при переносе. */
const previousDraftKey = 'fulfillment-draft-v3'
const photoLostKey = 'fulfillment-draft-photo-lost-v3'
const databaseName = 'evirma-fulfillment-draft'
const storeName = 'files'

/**
 * Черновик живёт в localStorage: он должен дождаться возвращения даже после закрытия вкладки.
 * Хранилище отказывает в приватном окне, при запрете данных сайта и когда кончилось место, причём
 * отказом на любом обращении. Ронять из-за этого форму нельзя, поэтому каждое обращение отвечает
 * запасным значением: для записи — что она не удалась, для чтения — что черновика нет.
 */
const tryStorage = <T>(operation: () => T, whenUnavailable: T) => {
  try {
    return operation()
  } catch {
    return whenUnavailable
  }
}

/**
 * Признак «фото черновика не сохранилось». Лежит рядом с самим черновиком, а не в состоянии
 * страницы: узнать о потере нужно после возвращения, когда страница уже открылась заново.
 */
const markDraftPhotoLost = () => tryStorage(() => localStorage.setItem(photoLostKey, '1'), undefined)

const forgetDraftPhotoLost = () => tryStorage(() => localStorage.removeItem(photoLostKey), undefined)

/** Отвечает, потерялось ли фото, и снимает признак: он нужен ровно один раз. */
export const takeDraftPhotoLost = () =>
  tryStorage(() => {
    const isLost = localStorage.getItem(photoLostKey) === '1'
    localStorage.removeItem(photoLostKey)

    return isLost
  }, false)

/**
 * Переносит черновик, сохранённый до переезда прайса на новый договор сервера. Все поля остаются,
 * прайс сбрасывается: он записан по прежнему договору («код услуги → цена») и восстановлению не
 * подлежит. Пишет в хранилище браузера и удаляет прежний черновик.
 *
 * Черновик, заведённый уже после переезда, не трогается: он свежее, и перезаписывать его прошлой
 * работой нельзя.
 */
export const movePreviousDraft = () =>
  tryStorage(() => {
    const previous = localStorage.getItem(previousDraftKey)
    if (previous === null) {
      return
    }

    if (localStorage.getItem(draftKey) === null) {
      const { values, step } = JSON.parse(previous) as DraftData
      delete values.servicePrices

      localStorage.setItem(draftKey, JSON.stringify({ values, step } satisfies DraftData))
    }

    localStorage.removeItem(previousDraftKey)
  }, undefined)

/** Отвечает, дошли ли поля до хранилища: не дошли — черновика после возвращения не будет. */
const saveDraftFields = (fields: unknown) =>
  tryStorage(() => {
    localStorage.setItem(draftKey, JSON.stringify(fields))

    return true
  }, false)

export const loadDraftFields = <T>() =>
  tryStorage(() => {
    const saved = localStorage.getItem(draftKey)

    return saved === null ? null : (JSON.parse(saved) as T)
  }, null)

const clearDraftFields = () => tryStorage(() => localStorage.removeItem(draftKey), undefined)

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(storeName)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

/**
 * Ответ отдаём только после того, как транзакция зафиксирована: успех самого запроса ещё не
 * значит, что запись легла на диск, и уход со страницы сразу после вызова её отменит.
 */
const fileOperation = async <T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>) => {
  const database = await openDatabase()
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode)
    const request = operation(transaction.objectStore(storeName))
    request.onerror = () => reject(request.error)
    transaction.onabort = () => reject(transaction.error)
    transaction.oncomplete = () => {
      database.close()
      resolve(request.result)
    }
  })
}

const saveDraftFile = (file: File) => fileOperation('readwrite', (store) => store.put(file, 'cover'))
export const loadDraftFile = () => fileOperation<File | undefined>('readonly', (store) => store.get('cover'))

/**
 * Убирает фото черновика и молчит об отказе хранилища: работа с фото не должна ронять сохранение
 * полей — без фото черновик всё равно полезен.
 */
const forgetDraftFile = () => fileOperation('readwrite', (store) => store.delete('cover')).catch(() => undefined)

/** Записывает поля с номером шага. Отвечает, дошли ли они до хранилища. */
export const keepDraftFields = (values: FulfillmentFormValues, step: number) =>
  saveDraftFields({ values, step } satisfies DraftData)

/**
 * Записывает черновик целиком: поля с номером шага — в localStorage, обложку — в IndexedDB.
 * Отвечает раздельно про поля и про фото, потому что хранилища отказывают независимо друг от друга
 * (приватное окно, запрет данных сайта, кончилось место). Не дошедшее фото убираем и из черновика,
 * иначе после возвращения в карточку уехало бы старое, а владелец считал бы, что выбрал новое.
 *
 * Обложку записывает только когда `cover` передан: при автосохранении файл пишется отдельно и реже,
 * чем поля. Незаполненную форму сохранять можно — черновик ждёт возвращения и ничего не проверяет.
 */
export const keepDraft = async (values: FulfillmentFormValues, step: number, cover: File | null) => {
  const areFieldsKept = keepDraftFields(values, step)
  forgetDraftPhotoLost()
  if (!cover) {
    await forgetDraftFile()

    return { areFieldsKept, isPhotoKept: true }
  }

  try {
    await saveDraftFile(cover)

    return { areFieldsKept, isPhotoKept: true }
  } catch {
    await forgetDraftFile()
    markDraftPhotoLost()

    return { areFieldsKept, isPhotoKept: false }
  }
}

/** Убирает черновик целиком: и поля, и обложку, и признак потерянного фото. */
export const clearDraft = async () => {
  clearDraftFields()
  forgetDraftPhotoLost()
  await forgetDraftFile()
}
